import {
  AutoGroupColumnDef,
  ColDef,
  ColGroupDef,
  IAggFuncParams,
  ValueFormatterParams,
} from "ag-grid-community";
import {
  currencyFormatter,
  isValidNumber,
  numberFormatter,
} from "@platform/fe-utils";
import { z } from "zod";

import { formatDate } from "../../utils";
import GuardianLogo from "../../assets/Guardian.svg";
import LincolnLogo from "../../assets/Lincoln.svg";
import PrincipalLogo from "../../assets/Principal.svg";

import { CarrierLinkCell } from "./components/CarrierLinkCell";
import { ScenarioEditType } from "./types";
import { BrokerSummary, CarrierBonusSummaryApi, LocCombinedApi } from "./types";

export const CARRIER_BONUS_SUMMARY_TABLE_COLUMN_HEADERS: ColDef<CarrierBonusSummaryApi>[] =
  [
    {
      headerName: "Carrier",
      field: "carrier_name",
      valueFormatter: ({ value }: { value: string | null }) => value ?? "N/A",
      sortable: false,
      cellRenderer: CarrierLinkCell,
    },
    {
      headerName: "Carrier Update Date",
      field: "carrier_update_date",
      valueFormatter: ({ value }: { value: string | null }) => value ?? "N/A",
      sortable: false,
    },
    {
      headerName: "Persistency",
      field: "persistency_percentage",
      valueFormatter: ({ value }) =>
        value ? `${(value * 100).toFixed(2)}%` : "N/A",
      sortable: false,
    },
    {
      headerName: "Persistency Qualification Level",
      field: "persistency_tier_level",
      valueFormatter: ({ value }: { value: string | null }) => value ?? "N/A",
      sortable: false,
    },
    {
      headerName: "Persistency Projected Bonus",
      field: "persistency_projected_bonus",
      valueFormatter: ({ value }) =>
        value != null
          ? `$${numberFormatter({ v: value as number, minDecimal: 0, maxDecimal: 0 })}`
          : "N/A",
      sortable: false,
    },
    {
      headerName: "New Business Premium",
      field: "new_business_premium",
      valueFormatter: ({ value }) =>
        value != null
          ? `$${numberFormatter({ v: value as number, minDecimal: 0, maxDecimal: 0 })}`
          : "N/A",
      sortable: false,
    },
    {
      headerName: "New Business LOC Credits",
      field: "new_business_loc_credits",
      valueFormatter: ({ value }: { value: number | null }) =>
        value != null
          ? numberFormatter({
              v: value as number,
              minDecimal: 0,
              maxDecimal: 1,
            })
          : "N/A",
      sortable: false,
    },
    {
      headerName: "New Business Qualification Level",
      field: "new_business_tier_level",
      valueFormatter: ({ value }: { value: string | null }) => value ?? "N/A",
      sortable: false,
    },
    {
      headerName: "New Business Projected Bonus",
      field: "new_business_bonus_amount",
      valueFormatter: ({ value }) =>
        value != null
          ? `$${numberFormatter({ v: value as number, minDecimal: 0, maxDecimal: 0 })}`
          : "N/A",
      sortable: false,
    },
    {
      headerName: "Total Projected Bonus",
      field: "total_projected_bonus",
      valueFormatter: ({ value }) =>
        value != null
          ? `$${numberFormatter({ v: value as number, minDecimal: 0, maxDecimal: 0 })}`
          : "N/A",
      sortable: false,
    },
  ];

export const CARRIER_NAME = {
  estimatedBonus: "Estimated Bonus",
} as const;

export type CarrierKey = "guardian" | "lincoln" | "principal";
export type CarrierName =
  | "Guardian"
  | "Lincoln National"
  | "Principal Financial Group";

export const CARRIER_NAME_MAP = {
  Guardian: "guardian",
  "Lincoln National": "lincoln",
  "Principal Financial Group": "principal",
} as const satisfies Record<CarrierName, CarrierKey>;

export type CarrierKeyForName<N extends CarrierName> =
  (typeof CARRIER_NAME_MAP)[N];

export const CARRIER_ASSETS = {
  guardian: GuardianLogo,
  lincoln: LincolnLogo,
  principal: PrincipalLogo,
} satisfies Record<CarrierKey, string>;

export interface CarrierBrokerSummary {
  carrierId: string;
  carrierName: string;
  brokerSummaries: BrokerSummary[];
}

// Default color palette if a series doesn't specify one
export const BAR_CHART_DEFAULT_COLORS = [
  "#76B8D8", // light teal
  "#3D628B", // navy
  "#7C3D8C", // purple
  "#F3AA4E", // orange
  "#4FBF9C", // green
];

export const CARRIER_BONUS_DETAIL_PERSISTENCY_TITLE = [
  {
    title: "Persistency Projected Bonus",
    field: "persistency_projected_bonus",
  },
  { title: "Persistency Start Book", field: "persistency_start_book" },
  { title: "Terminated Premium", field: "term_prem" },
  { title: "Persistency", field: "persistency_percentage" },
  { title: "Persistency Qualification Level", field: "persistency_tier_level" },
  {
    title: "Bonus Eligible Persistency Premium",
    field: "bonus_eligible_persistency_premium",
  },
];

export const CARRIER_BONUS_DETAIL_BUSINESS_TITLE = [
  { title: "New Business Projected Bonus", field: "new_business_bonus_amount" },
  { title: "New Business Premium", field: "new_business_premium" },
  { title: "New Business LOC Credits", field: "new_business_loc_credits" },
  {
    title: "New Business Qualification Level",
    field: "new_business_tier_level",
  },
  {
    title: "Bonus Eligible New Business Premium",
    field: "bonus_eligible_new_business_premium",
  },
];

export const FIELD_FORMATTERS: Record<
  string,
  { type: "integer" | "percent"; decimals?: number }
> = {
  new_business_loc_credits: { type: "integer" },
  persistency_percentage: { type: "percent", decimals: 2 },
  persistency: { type: "percent", decimals: 2 },
  nbLocCredits: { type: "integer" },
};

export const IN_OUT_TABLE_COLUMN_HEADERS: ColDef<LocCombinedApi>[] = [
  {
    headerName: "Annual Premium",
    field: "annualized_premium",
    sortable: false,
    valueFormatter: ({ value }) => currencyFormatter(value),
  },
  {
    headerName: "Starting Premium",
    field: "starting_premium",
    sortable: false,
    valueFormatter: ({ value }) => currencyFormatter(value),
  },
  {
    headerName: "Eligible Premium",
    field: "eligible_premium",
    sortable: false,
    valueFormatter: ({ value }) => currencyFormatter(value),
  },
];

export const IN_OUT_TABLE_GROUP_COLUMN_HEADER: AutoGroupColumnDef = {
  headerName: "In/Out Type",
  sortable: false,
  cellRendererParams: {
    suppressCount: true,
  },
};

export const CLASSIFICATION_DISPLAY_NAME: Record<string, string> = {
  "New BOR": "New BOR",
  "New Sale": "New Sale",
  "Terminated - Existing": "Terminated",
  "Lost BOR - Existing": "Lost BOR",
  "Lost BOR - New": "Lost BOR",
};

const strictIntAllowZero = z.preprocess(
  (val) => (val === "" ? NaN : Number(val)),
  z.coerce.number().int(),
) as z.ZodType<number, z.ZodTypeDef, number>;
const strictInt = z.preprocess(
  (val) => (val === "" ? NaN : Number(val)),
  z.coerce.number().gt(0).int(),
) as z.ZodType<number, z.ZodTypeDef, number>;

export const CARRIER_BROKER_SETTING_FORM_SCHEMA = z.object({
  paidBonus: strictIntAllowZero,
  beginningBookSize: strictInt,
  locCount: strictInt,
  bonusGoal: strictIntAllowZero,
  carrierRuleId: z.string(),
});

export const SCENARIO_MODEL_MODE = {
  Create: "create",
  RenameModelName: "rename-model-name",
  Duplicate: "duplicate",
  Add: "add",
  Remove: "remove",
  RenameScenarioName: "rename-scenario-name",
  RemoveMigrateChange: "remove-scenario-migrate-change",
  RemoveChangePartialMigrate: "remove-scenario-partial-migrate",
} as const;

export type ScenarioModelModeType =
  (typeof SCENARIO_MODEL_MODE)[keyof typeof SCENARIO_MODEL_MODE];

const scenarioModalLabel = "Scenario Name";
const removeChanges = "Remove Changes";

export const MODAL_CONFIG: Record<
  ScenarioModelModeType,
  {
    title: string;
    description: string;
    confirmButtonLabel: string;
    inputLabel?: string;
    showInput?: boolean;
    additional?: string;
  }
> = {
  [SCENARIO_MODEL_MODE.Create]: {
    title: "Create New Model",
    description: `This will create a new model using the most current data and the currently active ruleset. Existing scenarios will not be copied to the new model, so you will need to create new scenarios.\n\nThe current ruleset can be viewed in Carrier Bonus Settings.`,
    confirmButtonLabel: "Create",
  },
  [SCENARIO_MODEL_MODE.RenameModelName]: {
    title: "Rename Model",
    description: "Enter new model name.",
    confirmButtonLabel: "Rename",
  },
  [SCENARIO_MODEL_MODE.Duplicate]: {
    title: "Duplicate Scenario",
    description:
      "Add a new scenario with Very Best Case Scenario data as the starting point.",
    confirmButtonLabel: "Duplicate",
    inputLabel: scenarioModalLabel,
  },
  [SCENARIO_MODEL_MODE.Add]: {
    title: "Add Scenario",
    description:
      "Add a new scenario with selected model data as the starting point.",
    confirmButtonLabel: "Add",
    inputLabel: scenarioModalLabel,
  },
  [SCENARIO_MODEL_MODE.RenameScenarioName]: {
    title: "Rename Scenario",
    description: "Enter new scenario name.",
    confirmButtonLabel: "Rename",
    inputLabel: scenarioModalLabel,
  },
  [SCENARIO_MODEL_MODE.Remove]: {
    title: "Remove Scenario",
    description:
      "Are you sure you want to delete this scenario? This cannot be undone.",
    confirmButtonLabel: "Yes, Remove",
    showInput: false,
  },
  [SCENARIO_MODEL_MODE.RemoveMigrateChange]: {
    title: "Remove Migration Changes",
    description:
      "Migration changes have other records with changes associated with them.",
    confirmButtonLabel: removeChanges,
    showInput: false,
    additional: "Do you want to remove these changes?",
  },
  [SCENARIO_MODEL_MODE.RemoveChangePartialMigrate]: {
    title: removeChanges,
    description:
      "Some of the selected changes are migration changes that have other records with changes associated with them.",
    confirmButtonLabel: removeChanges,
    showInput: false,
    additional: "Do you want to remove these changes?",
  },
};

const SCENARIO_EDIT_TYPE_LABELS: Record<ScenarioEditType, string> = {
  [ScenarioEditType.Migrate]: "Migrate",
  [ScenarioEditType.Terminate]: "Terminate",
  [ScenarioEditType.LostBOR]: "Lost BOR",
  [ScenarioEditType.AddNew]: "Add New",
};

export const SCENARIO_DETAILS_COLUMN_HEADER: (ColGroupDef | ColDef)[] = [
  {
    headerName: "Scenario Change",
    field: "scenarioChange",
    aggFunc: (params: IAggFuncParams): number =>
      params.values.filter(Boolean).length,
    valueFormatter: ({ value, node, data }: ValueFormatterParams): string => {
      if (node?.group) {
        const changeCount = typeof value === "number" ? value : 0;
        return changeCount > 0 ? `${changeCount} Change(s)` : "-";
      }
      if (!value) return "-";
      if (value === ScenarioEditType.Migrate) {
        if (data?.migrateToCarrierName != null)
          return `Migrated to ${data.migrateToCarrierName}`;
        if (data?.migrateFromCarrierName != null)
          return `Migrated from ${data.migrateFromCarrierName}`;
        return "Migrate (unknown carrier)";
      }
      return SCENARIO_EDIT_TYPE_LABELS[value as ScenarioEditType] ?? "-";
    },
  },
  {
    headerName: "Group",
    children: [
      {
        headerName: "Group Name",
        field: "clientName",
        rowGroup: true,
        cellRenderer: "agGroupCellRenderer",
        showRowGroup: true,
        filter: "agSetColumnFilter",
      },
      { headerName: "Group Number", field: "productNo" },
    ],
  },
  { headerName: "Carrier", field: "carrierName" },
  {
    headerName: "Coverage",
    children: [
      { headerName: "Policy Type", field: "policyType" },
      { headerName: "Plan Type", field: "planType" },
      { headerName: "Status", field: "status" },
      {
        headerName: "Status Date",
        field: "statusDate",
        valueFormatter: ({ value }) => formatDate(value, true),
      },
      {
        headerName: "Average Monthly Premium",
        field: "averageMonthlyPremiumOverride",
        valueFormatter: ({ value, node }) =>
          node?.group ? "" : currencyFormatter(value ?? 0, ""),
      },
      {
        headerName: "Renewal Date",
        field: "renewalDate",
        valueFormatter: ({ value }) => formatDate(value, true),
      },
      {
        headerName: "Original Sale Date",
        field: "originalPlanSaleDate",
        valueFormatter: ({ value }) => formatDate(value, true),
      },
      {
        headerName: "Premium Eligibility Date",
        field: "premiumEligibleDate",
        valueFormatter: ({ value }) => formatDate(value, true),
      },
      { headerName: "Funding Type", field: "contractType" },
      { headerName: "Participants", field: "participants" },
      { headerName: "Broker Split", field: "brokerSplit" },
      { headerName: "Eligibility Status", field: "bonusEligibilityStatus" },
    ],
  },
];

export const SCENARIO_ACTION_DRAWER_TYPE = {
  Terminate: "TERMINATE",
  LostBOR: "LOST_BOR",
  Migrate: "MIGRATE",
  AddNew: "ADD_NEW",
} as const;

export type ScenarioActionDrawerType =
  (typeof SCENARIO_ACTION_DRAWER_TYPE)[keyof typeof SCENARIO_ACTION_DRAWER_TYPE];

export const DRAWER_CONFIG: Record<
  ScenarioActionDrawerType,
  {
    title: string;
    confirmButtonLabel: string;
  }
> = {
  [SCENARIO_ACTION_DRAWER_TYPE.LostBOR]: {
    title: "Lost BOR",
    confirmButtonLabel: "Lost BOR",
  },
  [SCENARIO_ACTION_DRAWER_TYPE.Terminate]: {
    title: "Terminate",
    confirmButtonLabel: "Terminate",
  },
  [SCENARIO_ACTION_DRAWER_TYPE.Migrate]: {
    title: "Migrate",
    confirmButtonLabel: "Migrate",
  },
  [SCENARIO_ACTION_DRAWER_TYPE.AddNew]: {
    title: "Add New Business",
    confirmButtonLabel: "Next",
  },
};

const ADD_NEW_BUSINESS_FORM_FIELDS = z.object({
  businessType: z.string(), // maybe instead of businessType we can switch to isBOR?
  groupType: z.string().nullable().optional(),
  groupName: z.string().optional(),
  groupId: z
    .object({ brokerName: z.string(), brokerSk: z.string() })
    .nullable()
    .optional(),
});

export type GroupDetailsFormValues = z.infer<
  typeof ADD_NEW_BUSINESS_FORM_FIELDS
>;

export const ADD_NEW_BUSINESS_FORM_SCHEMA =
  ADD_NEW_BUSINESS_FORM_FIELDS.superRefine((data, ctx) => {
    const needsGroupName =
      data.businessType === "BOR" || data.groupType === "newGroup";

    if (needsGroupName && !data.groupName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Group name is required",
        path: ["groupName"],
      });
    }

    if (data.groupType === "existingGroup" && !data.groupId?.brokerSk) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Group is required",
        path: ["groupId"],
      });
    }
  });

const ADD_NEW_BUSINESS_COVERAGE_FIELDS = z.object({
  carrier: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .nullable()
    .refine((val) => val !== null && val.value !== "", {
      message: "Carrier is required",
    }),
  originalSaleDate: z
    .date()
    .nullable()
    .refine((val) => val !== null, {
      message: "Original sale date is required",
    }),
  premiumEligibleDate: z.date().nullable(),
  policyType: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .nullable()
    .refine((val) => val !== null, { message: "Policy type is required" }),
  planType: z
    .object({ id: z.string(), name: z.string() })
    .nullable()
    .refine((val) => val !== null, { message: "Plan type is required" }),
  averageMonthlyPremium: z
    .union([z.string(), z.number()])
    .refine((val) => val !== "" && !Number.isNaN(Number(val)), {
      message: "Average monthly premium is required",
    }),
  fundingType: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .nullable()
    .refine((val) => val !== null, { message: "Funding type is required" }),
  participants: z
    .union([z.string(), z.number()])
    .nullable()
    .refine(isValidNumber, { message: "Participants is required" }),
  brokerSplit: z
    .union([z.string(), z.number()])
    .nullable()
    .refine(isValidNumber, { message: "Broker split is required" })
    .refine((val) => !isValidNumber(val) || Number(val) >= 0, {
      message: "Broker split cannot be negative",
    })
    .refine((val) => !isValidNumber(val) || Number(val) <= 100, {
      message: "Broker split cannot exceed 100%",
    }),
});

export const createAddNewBusinessCoverageSchema = (
  businessType: string,
): z.ZodEffects<typeof ADD_NEW_BUSINESS_COVERAGE_FIELDS> =>
  ADD_NEW_BUSINESS_COVERAGE_FIELDS.superRefine((data, ctx) => {
    if (businessType === "BOR" && data.premiumEligibleDate === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Premium eligible date is required",
        path: ["premiumEligibleDate"],
      });
    }
  });

export type CoverageDetailsFormValues = z.input<
  typeof ADD_NEW_BUSINESS_COVERAGE_FIELDS
>;

export enum PolicyType {
  Dental = "POLICY_DENTAL",
  Disability = "POLICY_DISABILITY",
  Life = "POLICY_LIFE",
  "Long Term Care" = "POLICY_LONG_TERM_CARE",
  Medical = "POLICY_MEDICAL",
  Other = "POLICY_OTHER",
  "State Disability & Paid Family Leave" = "POLICY_STATE_DISABILITY",
  "Stop Loss" = "POLICY_STOP_LOSS",
  "Supplemental Health" = "POLICY_SUPPLEMENTAL_HEALTH",
  Vision = "POLICY_VISION",
}

export const POLICY_TYPE_TO_PRODUCT_NAMES: Record<string, string[]> = {
  [PolicyType.Dental]: [
    "Discount Only",
    "EPO",
    "HMO",
    "Indemnity",
    "Pediatric Dental",
    "POS",
    "PPO",
  ],
  [PolicyType.Disability]: [
    "Individual Disability Insurance (IDI)",
    "Long Term Disability (LTD)",
    "Short Term Disability (STD)",
  ],
  [PolicyType.Life]: [
    "AD&D Only",
    "Basic Life Only",
    "Basic Life with AD&D",
    "Universal Life",
    "Voluntary Life Only",
    "Voluntary Life with AD&D",
    "Whole Life",
  ],
  [PolicyType["Long Term Care"]]: ["N/A"],
  [PolicyType.Medical]: [
    "EPO",
    "Gap Coverage",
    "HMO",
    "Indemnity",
    "Minimum Essential Coverage (MEC)",
    "POS",
    "PPO",
    "Telemedicine",
  ],
  [PolicyType.Other]: [
    "Auto & Home",
    "Employee Assistance Program (EAP)",
    "FMLA",
    "Identity Theft",
    "Legal",
    "Medical Transport",
    "Pet Insurance",
    "Total Absence Management",
    "Wellness",
  ],
  [PolicyType["State Disability & Paid Family Leave"]]: [
    "CA PFL",
    "CA SDI",
    "CO FAMLI",
    "CT Paid Leave",
    "DE Paid Leave",
    "HI TDI",
    "MA PFML",
    "MD FAMLI",
    "ME PFML",
    "MN PFML",
    "Multi-State PFML",
    "NH PFML",
    "NJ FLI",
    "NJ TDI",
    "NY DBL",
    "NY PFL",
    "Paid Leave OR",
    "PR SINOT",
    "RI TCI",
    "RI TDI",
    "VT FAMLI",
    "WA PFML",
  ],
  [PolicyType["Stop Loss"]]: ["N/A"],
  [PolicyType["Supplemental Health"]]: [
    "Accident Insurance",
    "Cancer Insurance",
    "Critical Illness",
    "Hospital Indemnity",
  ],
  [PolicyType.Vision]: ["Discount Only", "Fee Schedule", "PPO"],
};

export enum FundingType {
  "Fully Insured" = "FUNDING_FULLY_INSURED",
  "Self Insured" = "FUNDING_SELF_INSURED",
}

export const DRAWER_ACTIONS: {
  type: ScenarioActionDrawerType;
  label: string;
}[] = [
  { type: SCENARIO_ACTION_DRAWER_TYPE.LostBOR, label: "Lost BOR" },
  { type: SCENARIO_ACTION_DRAWER_TYPE.Terminate, label: "Terminate" },
  { type: SCENARIO_ACTION_DRAWER_TYPE.Migrate, label: "Migrate" },
  { type: SCENARIO_ACTION_DRAWER_TYPE.AddNew, label: "New Business" },
];
